import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FlipHorizontal, Maximize, RotateCcw, SlidersHorizontal, X, ZoomIn, ZoomOut } from "lucide-react";
import { cn } from "@/lib/cn";

type Props = {
  src: string;
  alt?: string;
  fileName?: string;
  onClose?: () => void;
  onDownload?: () => void;
  embedded?: boolean;
};

type Preset = { nome: string; contraste: number; brilho: number; invertido: boolean };

const PRESETS: Preset[] = [
  { nome: "Normal", contraste: 100, brilho: 100, invertido: false },
  { nome: "Alto contraste", contraste: 180, brilho: 90, invertido: false },
  { nome: "Invertido", contraste: 100, brilho: 100, invertido: true },
];

/** Visualizador de imagem com zoom/pan/contraste/brilho — pensado pra radiografia (inverter cores
 *  equivale à visão em negativo), mas serve pra qualquer foto clínica. Canvas2D + CSS filter, sem
 *  dependência externa; portado de um visualizador equivalente já validado em outro projeto. */
export function ImagemViewer({ src, alt = "Imagem", fileName, onClose, onDownload, embedded }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(0.1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [flipX, setFlipX] = useState(1);

  const [brilho, setBrilho] = useState(100);
  const [contraste, setContraste] = useState(100);
  const [invertido, setInvertido] = useState(false);

  const [showControls, setShowControls] = useState(false);
  const [activePreset, setActivePreset] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const fitImageToContainer = useCallback(() => {
    if (!containerRef.current || !imageRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const imgWidth = imageRef.current.naturalWidth;
    const imgHeight = imageRef.current.naturalHeight;
    if (!imgWidth || !imgHeight) return;

    const imgRatio = imgWidth / imgHeight;
    const containerRatio = containerWidth / containerHeight;
    const newZoom = imgRatio > containerRatio ? (containerWidth * 0.9) / imgWidth : (containerHeight * 0.9) / imgHeight;

    setZoom(newZoom);
    setMinZoom(newZoom);
    setPanX(0);
    setPanY(0);
  }, []);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const image = imageRef.current;
    if (!canvas || !ctx || !image || !containerRef.current) return;

    const displayWidth = image.naturalWidth * zoom;
    const displayHeight = image.naturalHeight * zoom;
    canvas.width = displayWidth;
    canvas.height = displayHeight;
    canvas.style.left = `calc(50% + ${panX}px)`;
    canvas.style.top = `calc(50% + ${panY}px)`;
    canvas.style.transform = `translate(-50%, -50%) rotate(${rotation}deg) scaleX(${flipX})`;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let filtro = `contrast(${contraste}%) brightness(${brilho}%)`;
    if (invertido) filtro += " invert(1)";
    ctx.filter = filtro;
    ctx.drawImage(image, 0, 0, displayWidth, displayHeight);
  }, [zoom, panX, panY, rotation, flipX, contraste, brilho, invertido]);

  useEffect(() => {
    const img = new Image();
    // "anonymous", não "use-credentials": a URL já é presignada (auth vai na query string), não
    // depende de cookie — pedir credencial cross-origin pro bucket R2 só quebraria sem necessidade.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      setTimeout(() => fitImageToContainer(), 50);
    };
    img.onerror = () => setImageLoaded(true);
    img.src = src;
  }, [src, fitImageToContainer]);

  useEffect(() => {
    if (imageLoaded) render();
  }, [imageLoaded, render]);

  useEffect(() => {
    const handleResize = () => render();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [render]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case "+":
        case "=":
          setZoom((prev) => Math.min(10, prev * 1.2));
          break;
        case "-":
          setZoom((prev) => Math.max(minZoom, prev * 0.8));
          break;
        case "0":
          fitImageToContainer();
          break;
        case "r":
        case "R":
          setRotation((prev) => prev - 90);
          break;
        case "i":
        case "I":
          setInvertido((prev) => !prev);
          break;
        case "Escape":
          onClose?.();
          break;
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [minZoom, fitImageToContainer, onClose]);

  function handleMouseDown(e: React.MouseEvent) {
    if (e.button === 0) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
    }
  }
  function handleMouseMove(e: React.MouseEvent) {
    if (isDragging) {
      setPanX(e.clientX - dragStart.x);
      setPanY(e.clientY - dragStart.y);
    }
  }
  function handleMouseUp() {
    setIsDragging(false);
  }

  function handleWheel(e: React.WheelEvent) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(minZoom, Math.min(10, zoom * delta));
    if (newZoom === zoom) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mouseX = e.clientX - rect.left - rect.width / 2 - panX;
    const mouseY = e.clientY - rect.top - rect.height / 2 - panY;
    setPanX((prev) => prev - mouseX * (newZoom / zoom - 1));
    setPanY((prev) => prev - mouseY * (newZoom / zoom - 1));
    setZoom(newZoom);
  }

  function applyPreset(index: number) {
    const preset = PRESETS[index];
    setContraste(preset.contraste);
    setBrilho(preset.brilho);
    setInvertido(preset.invertido);
    setActivePreset(index);
  }

  function handleReset() {
    setContraste(100);
    setBrilho(100);
    setInvertido(false);
    setRotation(0);
    setFlipX(1);
    setActivePreset(0);
    fitImageToContainer();
  }

  function toggleFullscreen() {
    if (!containerRef.current) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void containerRef.current.requestFullscreen();
  }

  const btnFlutuante =
    "flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-[rgba(26,26,30,0.95)] text-white backdrop-blur transition-colors hover:bg-white/15";

  return (
    <div className="relative h-full w-full overflow-hidden rounded-lg bg-[#0a0a14]">
      <div
        ref={containerRef}
        className={cn("relative h-full w-full overflow-hidden", isDragging ? "cursor-grabbing" : "cursor-grab")}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <canvas ref={canvasRef} className="absolute origin-center" aria-label={alt} />
      </div>

      {onClose && !embedded && (
        <button type="button" onClick={onClose} className={cn(btnFlutuante, "absolute left-3 top-3 hover:bg-red-600/80")} aria-label="Fechar">
          <X className="h-4 w-4" />
        </button>
      )}

      <div className="absolute right-3 top-3 flex flex-col items-end gap-2">
        <button
          type="button"
          onClick={() => setShowControls((v) => !v)}
          className={cn(btnFlutuante, showControls && "border-brand-400 bg-brand-600/40")}
          title="Ajustes"
          aria-label="Ajustes"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </button>

        {showControls && (
          <div className="flex w-56 flex-col gap-2">
            {/* Painel flutuante — fundo opaco (rigid rule), nunca translúcido puro. */}
            <div className="rounded-xl border border-white/10 bg-[rgba(26,26,30,0.97)] p-3 backdrop-blur">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/60">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Ajuste de imagem
              </p>

              <label className="mb-2 block">
                <span className="mb-1 flex justify-between text-[12px] text-white/70">
                  <span>Contraste</span>
                  <span className="rounded bg-brand-600/20 px-1.5 text-brand-300">{contraste}%</span>
                </span>
                <input type="range" min={0} max={300} value={contraste} onChange={(e) => setContraste(Number(e.target.value))} className="w-full accent-brand-500" />
              </label>

              <label className="mb-3 block">
                <span className="mb-1 flex justify-between text-[12px] text-white/70">
                  <span>Brilho</span>
                  <span className="rounded bg-brand-600/20 px-1.5 text-brand-300">{brilho}%</span>
                </span>
                <input type="range" min={0} max={200} value={brilho} onChange={(e) => setBrilho(Number(e.target.value))} className="w-full accent-brand-500" />
              </label>

              <div className="flex gap-1">
                {PRESETS.map((preset, i) => (
                  <button
                    key={preset.nome}
                    type="button"
                    onClick={() => applyPreset(i)}
                    className={cn(
                      "flex-1 rounded-md border px-1 py-1.5 text-center text-[10px] transition-colors",
                      activePreset === i ? "border-brand-500 bg-brand-600 text-white" : "border-white/10 bg-white/5 text-white/60 hover:text-white",
                    )}
                  >
                    {preset.nome}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[rgba(26,26,30,0.97)] p-3 backdrop-blur">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-white/60">Ferramentas</p>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setZoom(Math.min(10, zoom * 1.3))} className="flex flex-1 basis-[28%] flex-col items-center gap-1 rounded-lg border border-white/10 bg-white/5 py-1.5 text-white/70 hover:bg-white/10 hover:text-white">
                  <ZoomIn className="h-4 w-4" />
                  <span className="text-[10px]">Zoom +</span>
                </button>
                <button type="button" onClick={() => setZoom(Math.max(minZoom, zoom * 0.7))} className="flex flex-1 basis-[28%] flex-col items-center gap-1 rounded-lg border border-white/10 bg-white/5 py-1.5 text-white/70 hover:bg-white/10 hover:text-white">
                  <ZoomOut className="h-4 w-4" />
                  <span className="text-[10px]">Zoom -</span>
                </button>
                <button type="button" onClick={handleReset} className="flex flex-1 basis-[28%] flex-col items-center gap-1 rounded-lg border border-white/10 bg-white/5 py-1.5 text-white/70 hover:bg-white/10 hover:text-white">
                  <RotateCcw className="h-4 w-4" />
                  <span className="text-[10px]">Reiniciar</span>
                </button>
                <button type="button" onClick={() => setRotation((r) => r - 90)} className="flex flex-1 basis-[28%] flex-col items-center gap-1 rounded-lg border border-white/10 bg-white/5 py-1.5 text-white/70 hover:bg-white/10 hover:text-white">
                  <RotateCcw className="h-4 w-4 -scale-x-100" />
                  <span className="text-[10px]">Girar</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFlipX((f) => f * -1)}
                  className={cn(
                    "flex flex-1 basis-[28%] flex-col items-center gap-1 rounded-lg border py-1.5",
                    flipX === -1 ? "border-brand-500 bg-brand-600/20 text-white" : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white",
                  )}
                >
                  <FlipHorizontal className="h-4 w-4" />
                  <span className="text-[10px]">Espelhar</span>
                </button>
                <button type="button" onClick={toggleFullscreen} className="flex flex-1 basis-[28%] flex-col items-center gap-1 rounded-lg border border-white/10 bg-white/5 py-1.5 text-white/70 hover:bg-white/10 hover:text-white">
                  <Maximize className="h-4 w-4" />
                  <span className="text-[10px]">Tela cheia</span>
                </button>
              </div>
            </div>

            {onDownload && (
              <button type="button" onClick={onDownload} className={cn(btnFlutuante, "self-end hover:border-emerald-500 hover:bg-emerald-600/30")} aria-label="Baixar">
                <Download className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {fileName && !embedded && (
        <div className="absolute bottom-3 left-3 rounded-md border border-white/10 bg-[rgba(26,26,30,0.9)] px-3 py-1.5 backdrop-blur">
          <strong className="text-[12px] text-white">{fileName}</strong>
        </div>
      )}

      {!embedded && (
        <div className="absolute bottom-3 right-3 rounded-md border border-white/10 bg-[rgba(26,26,30,0.9)] px-3 py-1.5 backdrop-blur">
          <span className="text-[10px] text-white/50">Arraste pra mover · Scroll pra zoom · R: girar · I: inverter</span>
        </div>
      )}
    </div>
  );
}
